<template>
    <div>
        <div class="grid w-full grid-cols-12 gap-5">
            <div class="flex flex-col items-center col-span-6">
                <h4 class="font-semibold">
                    {{ lang('products::variant-prices.actions.export') }}
                </h4>

                <Button
                    :a="true"
                    :href="
                        route(`products/variant-prices.admin.export.xls`, {
                            id: props.variant.id,
                        })
                    "
                    dusk="grid-export"
                    color="default"
                    size="sm"
                >
                    {{ lang('products::variant-prices.actions.download') }}
                </Button>

                <p class="text-sm text-center">
                    {{ lang('products::variant-prices.actions.export_help') }}
                </p>
            </div>
            <div class="flex flex-col items-center col-span-6">
                <h4 class="font-semibold">
                    {{ lang('products::variant-prices.actions.import') }}
                </h4>

                <label for="import-file">
                    <Button dusk="grid-import" color="default" size="sm" type="button" @click="selectFile">
                        {{ lang('products::variant-prices.actions.upload') }}
                    </Button>
                </label>

                <p class="text-sm text-center">
                    {{ lang('products::variant-prices.actions.import_help') }}
                </p>

                <div v-if="lastError" class="px-2 py-1 text-xs text-red-500">
                    {{ lastError }}
                </div>
            </div>
        </div>
        <div :class="{ 'animate-pulse': loading.preview }" class="mt-6">
            <div class="w-full overflow-y-auto max-h-96">
                <table
                    class="w-full text-xs border table-fixed border-neutral-300 dark:border-neutral-800 [&_td]:border [&_th]:border [&_td]:px-2 [&_td]:py-1 [&_th]:px-2 [&_th]:text-left [&_th]:py-1 [&_td]:border-neutral-300 [&_td]:dark:border-neutral-800 [&_th]:border-neutral-300 [&_th]:dark:border-neutral-800"
                >
                    <thead>
                        <tr>
                            <th>
                                {{ lang('products::variant-prices.fields.quantity') }}
                            </th>
                            <th>
                                {{ lang('products::variant-prices.fields.price') }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="price in prices" :key="price.quantity">
                            <td>
                                {{ price.quantity }}
                            </td>
                            <td>
                                {{ price.price }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <input id="import-file" ref="fileInput" type="file" class="invisible" @change="submit" />
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useForm } from '@inertiajs/vue3';
import { useToast } from '@src/Common/Composables/useToast';
import { Variant } from '@src/Products/Pages/types';

interface Props {
    variant: Variant;
    productId: number;
}

const props = withDefaults(defineProps<Props>(), {});

const prices = ref([]);
const fileInput = ref(null);
const lastError = ref(null);

const loading = ref({
    preview: false,
});

const form = useForm({
    file: null,
    product_variant_id: props.variant.id,
});

const selectFile = () => {
    fileInput.value.click();
};

const submit = () => {
    form.file = fileInput.value.files[0];

    const data = new FormData();
    data.append('file', form.file);
    data.append('product_variant_id', `${form.product_variant_id}`);
    data.append('_method', 'PUT');

    axios
        .post(route('products/variant-prices.admin.import.xls', { id: props.variant.id }), data, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
        .then(({ data }) => {
            useToast({ message: data.message, type: data.success ? 'success' : 'error' });

            if (!data.success) {
                lastError.value = data.message;
            } else {
                lastError.value = null;
            }
        })
        .finally(() => {
            load();
        });
};

const load = () => {
    loading.value.preview = true;
    axios.get(route('products/variant-prices.admin.preview', { id: props.variant.id })).then(({ data }) => {
        prices.value = data.prices;

        loading.value.preview = false;
    });
};

onMounted(() => {
    load();
});
</script>
